import type React from "react";

import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";

import { HorizontalBarChart02Icon } from "../Icons/HorizontalBarChart02Icon";
import { MediaXButton } from "../Media/MediaXButton";
import { EditPollModal, type PollData } from "./EditPollModal";

type PollPreviewProps = {
  poll: PollData;
  onDelete?: () => void;
} & (
  | {
      onEdit: (poll: PollData) => void;
      limitedEditing?: boolean;
    }
  | {
      onEdit?: never;
      limitedEditing?: never;
    }
);

export function PollPreview({ poll, onEdit, onDelete, limitedEditing }: PollPreviewProps): React.ReactNode {
  const [isEditPollModalOpened, editPollModalHandler] = useBool(false);
  const { t } = useTranslation();

  const Component = onEdit ? "button" : "div";

  return (
    <>
      <Inline align="center" gap="sm" wrap={false}>
        <Component
          type={onEdit ? "button" : undefined}
          className="flex h-media-input w-fit cursor-pointer items-center gap-space-sm rounded-lg border border-none bg-background-secondary p-space-md text-left transition-colors hocus:bg-background-tertiary/50"
          onClick={onEdit ? editPollModalHandler.setTrue : undefined}
        >
          <span className="flex justify-center text-text-primary">
            <HorizontalBarChart02Icon size={32} />
          </span>
          <span className="w-full">
            <Stack as="span" gap="xs" align="start">
              <Text variant="body-emphasis" measure="compact" overflow="line-clamp-one">
                {poll.question}
              </Text>
              <Text variant="caption" tone="secondary">
                {t("component.poll-preview.subtitle", { count: poll.options.length })}
              </Text>
            </Stack>
          </span>
        </Component>
        {onDelete && (
          <span className="shrink-0">
            <MediaXButton onClick={onDelete} title={t("common.action.delete")} />
          </span>
        )}
      </Inline>
      {onEdit && (
        <EditPollModal
          isOpened={isEditPollModalOpened}
          onOpenChange={editPollModalHandler.set}
          poll={poll}
          onConfirm={onEdit}
          limitedEditing={limitedEditing}
        />
      )}
    </>
  );
}
